import { INCOME_TICK_MS, OFFLINE, PARCEL_BALANCE, VISITORS, entityDef } from '../config/balancing';
import { plotParkScore } from '../spatial/plots';
import { isIncomeKind } from '../config/catalog';
import type { EntityKind, ParkState, PlacedEntity } from '../types';

export function canAfford(coins: number, cost: number): boolean {
  return coins >= cost;
}

export function maxLevel(kind: EntityKind): number {
  return entityDef(kind).maxLevel;
}

export function upgradeCost(kind: EntityKind, currentLevel: number): number | null {
  const def = entityDef(kind);
  if (currentLevel < 1 || currentLevel >= def.maxLevel) {
    return null;
  }
  const cost = def.upgradeCostFromLevel[currentLevel];
  if (cost === undefined || cost <= 0) {
    return null;
  }
  return cost;
}

export function revenuePerTick(entity: PlacedEntity): number {
  if (!isIncomeKind(entity.kind)) {
    return 0;
  }
  return entityDef(entity.kind).revenuePerTickByLevel[entity.level] ?? 0;
}

export function storageCap(entity: PlacedEntity): number {
  return entityDef(entity.kind).storageCapByLevel[entity.level] ?? 0;
}

export function entityParkScore(entity: PlacedEntity): number {
  return entityDef(entity.kind).parkScoreByLevel[entity.level] ?? 0;
}

export function computeParkScore(state: ParkState): number {
  let score = 0;
  for (const entity of state.entities) {
    score += entityParkScore(entity);
  }
  for (const parcelId of state.unlockedLandIds) {
    score += PARCEL_BALANCE[parcelId]?.parkScore ?? plotParkScore(parcelId);
  }
  return score;
}

export function visitorTargetCount(parkScore: number): number {
  const scaled = VISITORS.baseCount + Math.floor(parkScore / VISITORS.scorePerVisitor);
  return Math.max(0, Math.min(VISITORS.maxCount, scaled));
}

export function applyIncomeTicks(state: ParkState, tickCount: number): void {
  if (tickCount <= 0) {
    return;
  }
  for (const entity of state.entities) {
    const produced = revenuePerTick(entity) * tickCount;
    if (produced <= 0) {
      continue;
    }
    const cap = storageCap(entity);
    entity.storedCoins = Math.min(cap, entity.storedCoins + produced);
  }
}

export function collectStoredIncome(entity: PlacedEntity): number {
  const amount = Math.floor(entity.storedCoins);
  entity.storedCoins = 0;
  return amount;
}

/** Transfer stored income into coins without changing production rates or caps. */
export function collectAllStoredIncome(state: ParkState): number {
  let total = 0;
  for (const entity of state.entities) {
    if (entity.storedCoins <= 0) {
      continue;
    }
    total += collectStoredIncome(entity);
  }
  state.coins += total;
  return total;
}

export function computeOfflineIncome(
  state: ParkState,
  now: number,
): {
  elapsedMs: number;
  ticks: number;
  uncapped: number;
  granted: number;
} {
  const elapsedMs = Math.max(0, now - state.lastSaveTimestamp);
  const cappedElapsedMs = Math.min(elapsedMs, OFFLINE.maxElapsedSeconds * 1000);
  const ticks = Math.floor(cappedElapsedMs / INCOME_TICK_MS);
  let uncapped = 0;
  for (const entity of state.entities) {
    uncapped += revenuePerTick(entity) * ticks;
  }
  const granted = Math.min(uncapped, OFFLINE.incomeCapCoins);
  return { elapsedMs, ticks, uncapped, granted };
}

export function applyOfflineEarnings(
  state: ParkState,
  now: number,
): {
  elapsedMs: number;
  ticks: number;
  uncapped: number;
  granted: number;
} {
  const result = computeOfflineIncome(state, now);
  if (result.granted > 0) {
    state.coins += result.granted;
  }
  return result;
}
